/**
 * Namespace for common DOM utility methods
 */
import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

export class BFXDomHelper {
  /**
   * Get the offset properties for the specified HTML element. When an element has an offset parent, then position attributes must
   * be relative to the offset parent instead of the global window. This will return how much the current container is offset from
   * the main document window and the dimensions of the closest container (useful when clipping is enabled).
   *
   * We try and shortcut up the hierarchy using the offsetParent elements where possible until we get to the very root of the document.
   * Sometimes an element does not have an offsetParent if display is set to none - for this we still ascend the hierarchy using
   * oarentElement property.
   * The container height and width returned are those of the closest offset parent to the specified element
   * @returns Object of type { left: number, top: number, containerHeight: number, containerWidth: number }
   */
  public static getParentContainerOffsets(htmlElement: HTMLElement) {
    const retOffsetPosition = {
      left: 0,
      top: 0,
      containerHeight: window.innerHeight,
      containerWidth: window.innerWidth,
    };
    if (!(htmlElement instanceof HTMLElement)) {
      return retOffsetPosition;
    }

    let currentElement: Element = htmlElement;
    // First we find the first valid offsetParent property - offsetParent is not always set
    // so we ascend through the parent hierarchy to find the first reference
    while (currentElement instanceof HTMLElement && !currentElement.offsetParent) {
      currentElement = currentElement.parentElement;
    }

    // Don't start calcs until we find a contentVisibility style class
    let hasContentVisibility = false;

    while (currentElement) {
      if (currentElement instanceof HTMLElement) {
        if (!hasContentVisibility) {
          hasContentVisibility = currentElement.classList.contains("lm_item_container");
          if (hasContentVisibility) {
            retOffsetPosition.containerHeight = currentElement.clientHeight;
            retOffsetPosition.containerWidth = currentElement.clientWidth;
          }
        }
        if (hasContentVisibility) {
          // Add on additional offsets for this container
          retOffsetPosition.left += currentElement.offsetLeft;
          retOffsetPosition.top += currentElement.offsetTop;
        }
        if (currentElement.offsetParent) {
          currentElement = currentElement.offsetParent;
        } else {
          currentElement = currentElement.parentElement;
        }
      } else {
        currentElement = currentElement.parentElement;
      }
    }

    return retOffsetPosition;
  }

  /**
   * Retrieve a px value from a document style variable
   * @param varName
   * @param defValue
   * @returns
   */
  public static getVariablePx(document, varName: string, defValue: number): number {
    // Scrollbar width is defined in css variable
    let retValue = defValue;
    if (document) {
      const style = getComputedStyle(document.body);
      const variableValueStr = style.getPropertyValue(varName);
      if (variableValueStr) {
        const index = variableValueStr.indexOf("px");
        if (index > 0) {
          const value = Number(variableValueStr.substring(0, index));
          if (value > 0) {
            retValue = value;
          }
        } else {
          logger.out.error(
            `Expecting Px variable format - please check variable value ${variableValueStr}`,
          );
        }
      }
    }
    return retValue;
  }
}
